import { useElementShown } from '@hh.ru/analytics-js';
import professionInfoCardButtonClick from '@hh.ru/analytics-js-events/build/career_platform/profession/profession_info_card_button_click';
import professionInfoCardElementShown from '@hh.ru/analytics-js-events/build/career_platform/profession/profession_info_card_element_shown';
import { translation, format, TranslatedComponent } from '@hh.ru/front-static-app';
import { Button, Card, Tag, Text, Title, useBreakpoint } from '@hh.ru/magritte-ui';
import { useSelectorNonNullable } from '@hh.ru/redux-create-reducer';
import { SPALink } from '@hh.ru/redux-spa-middleware';

import { getProfessionRouteHref } from 'src/app/routesUtils';
import { ProfessionId } from 'src/types/profession';
import { getCurrencyRange } from 'src/utils/currency';
import { getTagStyleByIndex } from 'src/widgets/CareerTrack/utils';

import styles from './styles.less';

interface CardProfessionProps {
    selectedProfessionIndex: number | null;
    hideButtons?: boolean;
}

const TrlKeys = {
    salaryDescription: 'career-platform.widgets.careerTrack.professionCard.salaryDescription',
    toCourses: 'career-platform.widgets.careerTrack.professionCard.toCourses',
    toProfession: 'career-platform.widgets.careerTrack.professionCard.toProfession',
};

const CardProfession: TranslatedComponent<CardProfessionProps> = ({ selectedProfessionIndex, hideButtons, trls }) => {
    const targetProfessions = useSelectorNonNullable((state) => state.widgetCareerTrack.targetProfessionList || []);
    const targetProfession = selectedProfessionIndex !== null ? targetProfessions[selectedProfessionIndex] : null;
    const professionId = useSelectorNonNullable((state) => state.widgetCareerTrack.sourceProfession.id);

    const { isXS, isS } = useBreakpoint();
    const isMobile = isXS || isS;

    const handleClickToCourses = () => {
        if (targetProfession) {
            professionInfoCardButtonClick({
                buttonName: 'profession_info_card_course',
                professionId,
                professionCardId: targetProfession?.profession.id,
            });
        }
    };

    const handleClickToProfession = () => {
        if (targetProfession) {
            professionInfoCardButtonClick({
                buttonName: 'profession_info_card_detail',
                professionId,
                professionCardId: targetProfession?.profession.id,
            });
        }
    };

    const cardRef = useElementShown(professionInfoCardElementShown, {
        professionId,
        professionCardId: targetProfession?.profession.id as unknown as ProfessionId,
    });

    if (!targetProfession || selectedProfessionIndex === null) {
        return null;
    }

    return (
        <Card ref={cardRef} borderRadius={isMobile ? 16 : 24} padding={isMobile ? 16 : 24} showBorder>
            <span className={styles.tagWrapper}>
                {/* TODO: fix ts error after magritte-ui update */}
                {/* @ts-expect-error */}
                <Tag style={getTagStyleByIndex(targetProfessions.length, selectedProfessionIndex)}>
                    {targetProfession.probability}%
                </Tag>
            </span>
            <Title size={'small'} description={targetProfession.profession.description} Element="h5">
                {targetProfession.profession.name}
            </Title>
            <div className={styles.salary}>
                <Title size={'small'} Element="h5">
                    {getCurrencyRange(
                        targetProfession.salary.from,
                        targetProfession.salary.to,
                        targetProfession.salary.currency
                    )}
                </Title>
            </div>
            <Text typography="paragraph-4-regular" style="secondary">
                {format(trls[TrlKeys.salaryDescription], { '{0}': targetProfession.grade.name.toLowerCase() })}
            </Text>

            {!hideButtons && (
                <div className={styles.buttonGroup}>
                    <Button
                        Element={SPALink}
                        to={getProfessionRouteHref(targetProfession.profession.id, {
                            section: 'courses',
                        })}
                        isSeoLink
                        size="medium"
                        mode="primary"
                        onClick={handleClickToCourses}
                        stretched
                    >
                        {trls[TrlKeys.toCourses]}
                    </Button>
                    <Button
                        Element={SPALink}
                        to={getProfessionRouteHref(targetProfession.profession.id)}
                        isSeoLink
                        size="medium"
                        mode="secondary"
                        onClick={handleClickToProfession}
                        stretched
                    >
                        {trls[TrlKeys.toProfession]}
                    </Button>
                </div>
            )}
        </Card>
    );
};
export default translation(CardProfession);
